import { InputHTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IFormFieldProps } from '../../../IFormFieldProps';
import Converter from '../../../../util/Converter';
import LocalStorageGateway from '../../../../../gateway/Form/LocalStorageGateway';
import Receipt from '../../../../../domain/Receipt/Receipt';
import PreviewReceiptUseCase from '../../../../../useCase/Receipt/PreviewReceiptUseCase';
import FetchReceiptDetailGateway from '../../../../../gateway/Receipt/FetchReceiptDetailGateway';
import FieldErrorMessage from '../../messages/FieldErrorMessage';
import RemoveReceiptUseCase from '../../../../../useCase/Receipt/RemoveReceiptUseCase';
import LocalStorageReceiptGateway from '../../../../../gateway/Receipt/LocalStorageReceiptGateway';
import Modal from "../../../modal/Modal";
import useModal from "../../../modal/useModal";
import envVariable from "../../../../util/envVariable";
import Loader from "../../../loader/Loader";
import ErrorModal from '../../../modal/ErrorModal';
import { useFolderId } from '../../../../../infrastructure/hooks';
import IconReceipt from './icon/IconReceipt';
import { EIconReceiptType } from './icon/EIconReceiptType';
import { decrementProvided } from '../../../../../store/element/receipt_counter';
import { useAppDispatch } from '../../../../../store/hook';

interface IProps extends InputHTMLAttributes<HTMLInputElement>, IFormFieldProps {
  classes?: string,
  id: string
  receipts: Receipt[] | null
  readOnly?: boolean
  errorReceiptValidation?: any
  isRequired?: boolean
  updateReceipts?: (newReceipts: Receipt[]) => void
}

const ReceiptPreview = ({ id, receipts, readOnly, errorReceiptValidation, isRequired, updateReceipts }: IProps): JSX.Element => {
  const { t } = useTranslation()
  const { folderId } = useFolderId()
  const dispatch = useAppDispatch();

  const [isShowing, toggle] = useModal();
  const [contentModal, setContentModal] = useState<InputHTMLAttributes<string>>();
  const [modalSize, setModalSize] = useState<string>('modal');
  const [isLoading, setLoading] = useState<boolean>(false)
  const [isLoadingDelete, setLoadingDelete] = useState<boolean>(false)
  const [showReceiptId, setShowReceiptId] = useState('')
  const [receiptName, setReceiptName] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const removeFile = receiptToRemove => () => {
    setLoadingDelete(true)
    setShowReceiptId(receiptToRemove.receiptId)
    new RemoveReceiptUseCase(new FetchReceiptDetailGateway(), new LocalStorageReceiptGateway(folderId), new LocalStorageGateway(folderId)).execute(receiptToRemove, folderId ?? undefined).then(response => {
      if (response) {
        const newReceipts = receipts?.filter(receipt => receipt !== receiptToRemove)
        console.log("newReceipts", newReceipts)
        if (updateReceipts) {
          updateReceipts((newReceipts) ?? [])
          console.log("updateReceipts", newReceipts?.length)
          if (isRequired && newReceipts?.length === 0) {
            console.log("decrement provided", newReceipts)
            dispatch(decrementProvided())
          }
        }
      } else {
        setErrorMessage(t('ged.remove_failed'))
      }
    }).catch((error) => {
      if (error.errors?.length && error.errors[0]?.message?.length > 0) {
        setErrorMessage(t(error.errors[0].message))
      } else {
        setErrorMessage(t('ged.remove_failed'))
      }
      return null;
    }).finally(() => {
      setLoadingDelete(false)
    })
  }

  const show = receiptToShow => () => {
    new PreviewReceiptUseCase(new FetchReceiptDetailGateway()).execute(receiptToShow.receiptId, folderId ?? undefined).then(response => {
      if (response) {
        downloadFile(response, response.resource)
      } else {
        setErrorMessage(t('ged.download_failed'))
      }
    }).catch((error) => {
      setLoading(false)
      if (error.errors?.length && error.errors[0]?.message?.length > 0) {
        setErrorMessage(t(error.errors[0].message))
      } else {
        setErrorMessage(t('ged.download_failed'))
      }
      return null;
    })
  }

  const downloadFile = (receipt, dataBase64) => {
    const blob = new Converter().dataURItoBlob(dataBase64)
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = url;
    a.download = receipt.title;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  const previewReceipt = receipt => () => {
    setLoading(true)
    setShowReceiptId(receipt.receiptId)
    new PreviewReceiptUseCase(new FetchReceiptDetailGateway()).execute(receipt.receiptId, folderId ?? undefined).then(response => {
      if (response) {
        setLoading(false)
        setContentModal(buildModalContent(receipt, response))
        setReceiptName(response.title)
      } else {
        setErrorMessage(t('ged.download_failed'))
      }
    }).catch((error) => {
      setLoading(false)
      if (error.errors?.length && error.errors[0]?.message?.length > 0) {
        setErrorMessage(t(error.errors[0].message))
      } else {
        setErrorMessage(t('ged.download_failed'))
      }
      return null;
    })
  }

  const buildModalContent = (receipt, response) => {
    const resource = response.resourceFile
    const resourceIsImage = blobIsImage(resource)
    const dataBase64 = response.resource
    resourceIsImage ? setModalSize('modal') : setModalSize('modal-lg')
    if (resource.includes('application')) {
      if (resource.includes('application/pdf')) {
        toggle()

        return (
          <div key={receipt.id} className="pj-modal-content-center">
            <iframe src={resource} width="100%" height="500" />
          </div>
        )
      } else {
        downloadFile(receipt, dataBase64)
      }
    } else {
      toggle()
      switch (true) {
        case resource.includes('audio/'):
          return (
            <div key={receipt.id} className="pj-modal-content-center">
              <audio controls>
                <source src={resource} type="audio/mp3" />
              </audio>
            </div>
          )
        case resource.includes('video/'):
          return (
            <div key={receipt.id} className="pj-modal-content-center">
              <video controls width="640" height="360">
                <source src={resource} type="video/mp4" />
              </video>
            </div>
          )
        case resource.includes('image/'):
          return (
            <div key={receipt.id} className="pj-modal-content-center">
              <img src={resource} alt={receipt.title} />
            </div>
          )
        case resource.includes('text/'):
          return (
            <div key={receipt.id} className="pj-modal-content-center">
              <iframe src={resource} width="100%" height="500" />
            </div>
          )
      }
    }
  }

  const blobIsImage = (blobData: string) => {
    return blobData.includes('image/')
  }

  const isPreviewMode = () => {
    return envVariable('REACT_APP_PJ_PREVIEW_MODE')
  }

  const receiptElement = receipts?.filter((receipt) => {
    return receipt.elementId === id
  })

  return (
    <>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      {isShowing && isPreviewMode() &&
        <Modal title={t('pj.modal-preview-title')}
          description={receiptName}
          customClassName={modalSize}
          hide={toggle}>
          {contentModal}
        </Modal>
      }

      {(receiptElement && receiptElement.length > 0) && (
        <div className='receipt-preview-container'>
          {(receiptElement.map((receipt, index) => (
            (id === receipt.elementId &&
              <>
                <div className="dzu-previewContainer" key={`${receipt.elementId}-${index}`}>
                  {(isPreviewMode() && receipt?.resource &&
                    (
                      <div className="pj-container-min-preview pj-figure">
                        {(isLoading && showReceiptId === receipt.receiptId) && <Loader />}
                        {(!isLoading || showReceiptId !== receipt.receiptId) &&
                          <div className="button align-items-end button--small button--width-fit button--height-fit pj-file-min-preview button-enabled"
                            onClick={previewReceipt(receipt)}>
                            <IconReceipt type={EIconReceiptType.EYE} className={'pj-preview'} />
                          </div>
                        }
                      </div>
                    )
                  )}
                  <div className="upload-progress__wrapper">
                    <div className="button align-items-end button--small button--width-fit button--height-fit pj-file-min-preview button-enabled"
                      onClick={show(receipt)}>
                      {(isPreviewMode() && receipt?.resource &&
                        (
                          <IconReceipt type={EIconReceiptType.DOWNLOAD} className={'pj-download'} />
                        )
                      )}
                      &nbsp;
                    </div>
                    <div className='doc-name' style={{ alignContent: 'center' }}>
                      {receipt.title}
                    </div>
                    <div className="self-center">
                      {(isLoadingDelete && showReceiptId === receipt.receiptId) && <Loader />}
                      {(!isLoadingDelete || showReceiptId !== receipt.receiptId) &&
                        <div className={`button align-items-end button--small button--width-fit button--height-fit btn-delete ${readOnly && "readonly-element"}`}
                          title={t('button.remove')}
                          onClick={removeFile(receipt)}>
                          <IconReceipt type={EIconReceiptType.CROSS} color='var(--red-400)' />
                        </div>
                      }
                    </div>
                  </div>
                </div>
                {(() => {
                  const error = errorReceiptValidation.find(e => e.name === receipt.receiptId);
                  if (error) {
                    return <FieldErrorMessage type={error.type} message={error.message} />
                  }
                  return null;
                })()}
              </>
            )
          ))
          )}
        </div>
      )}
    </>
  )
}

export default ReceiptPreview;
